// MUNYA OS · Atajos de teclado tipo Linear · § 18.4-11 · A3
//
// Patrón: secuencia de 2 teclas (g, p) o tecla simple (c, ?). Detecta el primer
// keystroke como "leader" si pertenece al set conocido, abre un buffer de 1.2s,
// y al segundo keystroke ejecuta la acción.
//
// Atajos:
//   g+i  → Inicio
//   g+c  → Calendario
//   g+p  → Proyectos
//   g+r  → CRM (lead board)
//   g+t  → TRACK (proyecto activo) · si no hay, abre lista de proyectos
//   g+s  → Selección (proyecto activo) · idem
//   g+f  → Contabilidad
//   g+a  → Administración
//   g+m  → Almacén
//   c    → Crear contextual (depende de la ruta actual · dispara CustomEvent 'mn:create')
//   ?    → Abrir cheat-sheet
//   .    → Toggle focus mode
//   esc  → Cierra cualquier modal/overlay con preference 'no-bubble'
//
// El listener IGNORA si el foco está en un input/textarea/select/contenteditable.

const PATH_BY_KEY: Record<string, string> = {
  i: '/',
  c: '/calendario',
  p: '/proyectos',
  r: '/crm',
  t: '/proyectos', // los específicos del proyecto activo se gestionan en su ficha
  s: '/proyectos',
  f: '/contabilidad',
  a: '/administracion',
  m: '/almacen',
};

const LEADER_TIMEOUT_MS = 1200;

type Listener = () => void;

interface KeyboardShortcutsApi {
  init(navigate: (path: string) => void): () => void;
  onCheatSheetToggle(cb: Listener): () => void;
  onFocusToggle(cb: Listener): () => void;
  onCreate(cb: Listener): () => void;
}

function isEditable(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (el.isContentEditable) return true;
  return false;
}

export const keyboardShortcuts: KeyboardShortcutsApi = (() => {
  const cheatSheetListeners = new Set<Listener>();
  const focusListeners = new Set<Listener>();
  const createListeners = new Set<Listener>();

  function emit(set: Set<Listener>): void {
    set.forEach((cb) => {
      try {
        cb();
      } catch {
        // silent
      }
    });
  }

  return {
    init(navigate) {
      let leaderActive = false;
      let leaderTimer: number | null = null;

      function resetLeader(): void {
        leaderActive = false;
        if (leaderTimer !== null) {
          window.clearTimeout(leaderTimer);
          leaderTimer = null;
        }
      }

      function handler(e: KeyboardEvent): void {
        // Ignora si el foco está en un campo editable o si hay modificadores
        if (e.metaKey || e.ctrlKey || e.altKey) return;
        if (isEditable(e.target)) return;

        const key = e.key.toLowerCase();

        if (leaderActive) {
          // Segunda tecla de la secuencia g+X
          resetLeader();
          const path = PATH_BY_KEY[key];
          if (path) {
            e.preventDefault();
            navigate(path);
          }
          return;
        }

        // Leader 'g'
        if (key === 'g') {
          e.preventDefault();
          leaderActive = true;
          leaderTimer = window.setTimeout(resetLeader, LEADER_TIMEOUT_MS);
          return;
        }

        // Atajos de tecla simple
        if (key === '?') {
          e.preventDefault();
          emit(cheatSheetListeners);
          return;
        }
        if (key === '.') {
          e.preventDefault();
          emit(focusListeners);
          return;
        }
        if (key === 'c') {
          e.preventDefault();
          emit(createListeners);
          return;
        }
      }

      window.addEventListener('keydown', handler);
      return () => {
        window.removeEventListener('keydown', handler);
        resetLeader();
      };
    },

    onCheatSheetToggle(cb) {
      cheatSheetListeners.add(cb);
      return () => cheatSheetListeners.delete(cb);
    },
    onFocusToggle(cb) {
      focusListeners.add(cb);
      return () => focusListeners.delete(cb);
    },
    onCreate(cb) {
      createListeners.add(cb);
      return () => createListeners.delete(cb);
    },
  };
})();

export const SHORTCUT_CHEATSHEET: { keys: string; description: string; category: string }[] = [
  { keys: 'g, i', description: 'Ir a Inicio', category: 'Navegación' },
  { keys: 'g, c', description: 'Ir a Calendario', category: 'Navegación' },
  { keys: 'g, p', description: 'Ir a Proyectos', category: 'Navegación' },
  { keys: 'g, r', description: 'Ir a CRM', category: 'Navegación' },
  { keys: 'g, f', description: 'Ir a Contabilidad', category: 'Navegación' },
  { keys: 'g, a', description: 'Ir a Administración', category: 'Navegación' },
  { keys: 'g, m', description: 'Ir a Almacén', category: 'Navegación' },
  { keys: '⌘ + K', description: 'Buscador global', category: 'Acciones' },
  { keys: 'c', description: 'Crear (contextual)', category: 'Acciones' },
  { keys: '.', description: 'Toggle modo concentración', category: 'Vista' },
  { keys: '?', description: 'Mostrar atajos', category: 'Ayuda' },
  { keys: 'Esc', description: 'Cerrar modal / overlay', category: 'Vista' },
];
